<script lang="ts">
  import { onMount } from "svelte";
  import { api, type MyTaskItem } from "../lib/api";
  import { haptic } from "../lib/tg";

  type Range = "today" | "today_tomorrow" | "week" | "overdue" | "all";

  let {
    range = "today_tomorrow",
    role = "manager",
    onback,
    onhome,
  }: {
    range?: Range;
    role?: "director" | "manager" | "installer" | "integration" | "unknown";
    onback?: () => void;
    onhome?: () => void;
  } = $props();

  let items = $state<MyTaskItem[]>([]);
  let total = $state(0);
  let loading = $state(true);
  let error = $state<string | null>(null);
  let activeRange = $state<Range>(range);

  function dueShort(iso: string | null, overdueDays: number): string {
    if (!iso) return "—";
    const d = new Date(iso);
    const time = d.toLocaleTimeString("ru", { hour: "2-digit", minute: "2-digit" });
    if (overdueDays >= 1) return `просрочено ${overdueDays} дн`;
    const ms = d.getTime() - Date.now();
    if (ms < 0) return `просрочено · ${time}`;
    const today = new Date(); today.setHours(0,0,0,0);
    const taskDay = new Date(d); taskDay.setHours(0,0,0,0);
    const dayDiff = Math.round((taskDay.getTime() - today.getTime()) / 86400000);
    if (dayDiff === 0) return `сегодня ${time}`;
    if (dayDiff === 1) return `завтра ${time}`;
    return `${d.toLocaleDateString("ru", { day: "numeric", month: "short" })} ${time}`;
  }

  async function load() {
    loading = true;
    try {
      const r = await api.myTasks(activeRange);
      items = r.items;
      total = r.total;
    } catch (e) {
      error = e instanceof Error ? e.message : "ошибка";
    } finally {
      loading = false;
    }
  }

  let removing = $state<number | null>(null);
  let menuFor = $state<number | null>(null);

  function notifyDealChanged(leadAmoId: number | null) {
    if (!leadAmoId) return;
    window.dispatchEvent(new CustomEvent("kvin:deal-changed", { detail: { leadAmoId } }));
  }

  async function completeTask(t: MyTaskItem) {
    haptic("medium");
    removing = t.amo_id;
    menuFor = null;
    try {
      await api.completeTask(t.amo_id);
      items = items.filter((x) => x.amo_id !== t.amo_id);
      total = Math.max(0, total - 1);
      notifyDealChanged(t.lead_amo_id);
    } catch (e) {
      // soft fail
    } finally {
      removing = null;
    }
  }

  async function deleteTask(t: MyTaskItem) {
    if (!confirm(`Удалить задачу?\n\n«${t.text.slice(0, 80)}»\n\nЗадача будет закрыта в amoCRM с пометкой «Удалено через Квин» и исчезнет из всех списков.`)) {
      return;
    }
    haptic("medium");
    removing = t.amo_id;
    menuFor = null;
    try {
      await api.deleteTask(t.amo_id);
      items = items.filter((x) => x.amo_id !== t.amo_id);
      total = Math.max(0, total - 1);
      notifyDealChanged(t.lead_amo_id);
    } catch (e) {
      alert(`Не удалось удалить: ${e instanceof Error ? e.message : "ошибка"}`);
    } finally {
      removing = null;
    }
  }

  function setRange(v: Range) {
    haptic("light");
    activeRange = v;
    history.replaceState(null, "", `?view=my-tasks&range=${v}`);
    load();
  }

  onMount(load);

  const RANGE_LABELS: Record<string, string> = {
    today: "сегодня",
    today_tomorrow: "сегодня + завтра",
    week: "ближайшая неделя",
    overdue: "только просроченные",
    all: "все открытые",
  };
</script>

<div class="screen">
  <header class="hdr">
    <button class="back" onclick={() => onback?.()} aria-label="Назад">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
        stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
    </button>
    <div class="title">Мои задачи · {total}</div>
    {#if onhome}
      <button class="home" onclick={() => onhome?.()} aria-label="Главная">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"
          stroke-linecap="round" stroke-linejoin="round">
          <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"/>
        </svg>
      </button>
    {/if}
  </header>

  <div class="sort-bar">
    <div class="sort-label">Период: <b>{RANGE_LABELS[activeRange]}</b></div>
    <div class="chips">
      <button class="chip" class:active={activeRange === "today"} onclick={() => setRange("today")}>Сегодня</button>
      <button class="chip" class:active={activeRange === "today_tomorrow"} onclick={() => setRange("today_tomorrow")}>+ Завтра</button>
      <button class="chip" class:active={activeRange === "week"} onclick={() => setRange("week")}>Неделя</button>
      <button class="chip" class:active={activeRange === "overdue"} onclick={() => setRange("overdue")}>Просрочки</button>
      <button class="chip" class:active={activeRange === "all"} onclick={() => setRange("all")}>Все</button>
    </div>
  </div>

  {#if loading}
    <div class="loading">Загружаю…</div>
  {:else if error}
    <div class="error-box">{error}</div>
  {:else if items.length === 0}
    <div class="empty">На этот период задач нет</div>
  {:else}
    <section class="list">
      {#each items as t}
        <div class="row-wrap">
          {#if t.lead_amo_id}
            <a class="row" class:overdue={t.overdue_days > 0}
               href={`?deal=${t.lead_amo_id}`}>
              <div class="row-text">{t.text}</div>
              <div class="row-meta">
                <span class="row-due">{dueShort(t.complete_till, t.overdue_days)}</span>
                <span class="sep"></span>
                <span class="row-lead">{t.lead_name}</span>
              </div>
            </a>
          {:else}
            <div class="row floating" class:overdue={t.overdue_days > 0}>
              <div class="row-text">{t.text}</div>
              <div class="row-meta">
                <span class="row-due">{dueShort(t.complete_till, t.overdue_days)}</span>
                <span class="sep"></span>
                <span class="row-floating">без сделки</span>
              </div>
            </div>
          {/if}
          <button class="kebab" type="button" aria-label="Действия"
                  disabled={removing === t.amo_id}
                  onclick={() => menuFor = menuFor === t.amo_id ? null : t.amo_id}>
            {#if removing === t.amo_id}⋯{:else}⋮{/if}
          </button>
          {#if menuFor === t.amo_id}
            <div class="menu" onclick={(e) => e.stopPropagation()} role="menu">
              <button onclick={() => completeTask(t)} type="button">Снять (выполнить)</button>
              {#if role === "director"}
                <button onclick={() => deleteTask(t)} type="button" class="danger">Удалить</button>
              {/if}
              <button onclick={() => menuFor = null} type="button" class="ghost">Отмена</button>
            </div>
          {/if}
        </div>
      {/each}
    </section>
  {/if}
</div>

<style>
  .screen { padding-bottom: 80px; }
  .hdr {
    padding: calc(12px + var(--safe-top, 0px)) 16px 12px;
    display: flex; align-items: center; gap: 8px;
    border-bottom: 1px solid var(--border);
  }
  .back, .home {
    width: 36px; height: 36px; border-radius: 10px;
    display: flex; align-items: center; justify-content: center;
    color: var(--text-mute);
  }
  .back:active, .home:active { background: var(--bg-elev); }
  .back svg, .home svg { width: 20px; height: 20px; }
  .title {
    font-family: 'Manrope', sans-serif;
    font-size: 15px; font-weight: 700;
    flex: 1; text-align: center;
  }

  .sort-bar {
    padding: 12px 16px 8px;
    border-bottom: 1px solid var(--border);
    background: var(--bg-elev);
  }
  .sort-label {
    font-size: 11px; color: var(--text-dim);
    text-transform: uppercase; letter-spacing: 0.08em;
    margin-bottom: 8px; font-weight: 600;
  }
  .sort-label b {
    color: var(--ember);
    font-weight: 700;
    text-transform: none; letter-spacing: 0;
  }
  .chips { display: flex; gap: 8px; flex-wrap: wrap; }
  .chip {
    background: var(--bg);
    border: 1.5px solid var(--border-strong, var(--border));
    border-radius: 22px;
    padding: 9px 16px;
    color: var(--text);
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 120ms;
    font-family: 'Manrope', sans-serif;
    -webkit-tap-highlight-color: rgba(232,124,47,0.15);
  }
  .chip:active { transform: scale(0.95); }
  .chip.active {
    background: var(--ember);
    color: #fff;
    border-color: var(--ember);
    font-weight: 700;
    box-shadow: 0 2px 8px rgba(232,124,47,0.32);
  }

  .loading, .error-box, .empty { padding: 40px 16px; text-align: center; color: var(--text-mute); font-size: 13px; }
  .error-box { color: var(--danger); }

  .list { padding: 12px 16px; display: flex; flex-direction: column; gap: 8px; }
  .row-wrap {
    display: flex; gap: 8px; align-items: stretch;
  }
  .row {
    flex: 1; min-width: 0;
    display: block;
    background: var(--bg-elev);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 12px 14px;
    color: var(--text);
    text-decoration: none;
    box-shadow: var(--shadow-sm);
    transition: transform 80ms ease;
  }
  .row:active { transform: scale(0.985); }
  .row.overdue { border-left: 3px solid var(--danger); }
  .row.floating { border-left: 3px solid var(--text-dim); }
  .row-text {
    font-family: 'Manrope', sans-serif;
    font-weight: 600; font-size: 14px;
    line-height: 1.35;
  }
  .row-meta {
    margin-top: 6px;
    font-size: 11px; color: var(--text-mute);
    display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
  }
  .row-due { font-weight: 600; }
  .row.overdue .row-due { color: var(--danger); }
  .row-lead { color: var(--brass); font-weight: 600; }
  .row-floating {
    color: var(--text-dim);
    font-style: italic;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }
  .sep { width: 3px; height: 3px; background: var(--text-dim); border-radius: 50%; }

  .row-wrap { position: relative; }
  .kebab {
    flex-shrink: 0;
    align-self: stretch;
    width: 36px;
    background: var(--bg);
    border: 1.5px solid var(--border-strong, var(--border));
    border-radius: 12px;
    color: var(--text-mute);
    font-size: 22px; font-weight: 700;
    line-height: 1;
    cursor: pointer;
    transition: all 120ms;
  }
  .kebab:active { transform: scale(0.94); }
  .kebab:hover { color: var(--text); border-color: var(--ember); }
  .kebab:disabled { opacity: 0.5; cursor: wait; }
  .menu {
    position: absolute;
    right: 0; top: 100%;
    margin-top: 4px;
    background: var(--bg-elev);
    border: 1px solid var(--border);
    border-radius: 12px;
    box-shadow: var(--shadow-lg);
    z-index: 30;
    min-width: 200px;
    overflow: hidden;
  }
  .menu button {
    display: block; width: 100%;
    background: transparent;
    border: none;
    text-align: left;
    padding: 12px 14px;
    color: var(--text);
    font-family: 'Manrope', sans-serif;
    font-size: 14px;
    cursor: pointer;
  }
  .menu button:not(:last-child) { border-bottom: 1px solid var(--border); }
  .menu button:active { background: var(--bg-elev-2); }
  .menu button.danger { color: var(--danger); }
  .menu button.ghost { color: var(--text-mute); }
</style>
